import { MutableRefObject, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import Link from "next/link";
import router from "next/router";

import {
  AlignLeftOutlined,
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  DownOutlined,
  EyeOutlined,
  NumberOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { t, Trans } from "@lingui/macro";
import {
  Alert,
  Button,
  Col,
  Divider,
  Dropdown,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { isEmpty } from "lodash";
import omit from "lodash/omit";
import { v4 } from "uuid";

import FormPreviewDrawer from "components/drawers/form-preview-drawer";
import QuestionItem from "components/forms-view/question-item";
import AdditionalFieldModal from "components/modals/additional-field-modal";
import MoreButton from "components/more-button";
import { ActionMenu } from "components/navigations/header";

import { useApplicationContext } from "contexts/app";

import { AdditionalField, Form as NavirecForm, FormQuestion } from "types";

import {
  canDuplicate,
  convertIntoDraggableQuestion,
  convertIntoFormQuestion,
  DraggableQuestion,
  FormAction,
  getQuestionFieldOptions,
} from "utils/navirec-forms";

export const CHOICE_FIELD_TYPES = ["multiple_choice", "single_choice"];

// `t` macro gets executed before the locale is fully loaded
export const FIELD_TYPES: { key: FormQuestion["question_type"]; name: () => string; icon: ReactNode }[] = [
  { key: "text", name: () => t`Text input`, icon: <AlignLeftOutlined /> },
  { key: "number", name: () => t`Number`, icon: <NumberOutlined /> },
  { key: "single_choice", name: () => t`Single choice`, icon: <CheckCircleOutlined /> },
  { key: "multiple_choice", name: () => t`Multi choice`, icon: <CheckSquareOutlined /> },
  { key: "date", name: () => t`Date`, icon: <CalendarOutlined /> },
  { key: "time", name: () => t`Time`, icon: <ClockCircleOutlined /> },
  { key: "attachment", name: () => t`Attachment`, icon: <CloudUploadOutlined /> },
  { key: "boolean", name: () => t`Yes/No`, icon: <CheckCircleOutlined /> },
  { key: "vehicle", name: () => t`Vehicle`, icon: <CarOutlined /> },
  { key: "driver", name: () => t`Driver`, icon: <UserOutlined /> },
  { key: "vehicle_odometer", name: () => t`Vehicle odometer`, icon: <CarOutlined /> },
  { key: "vehicle_total_engine_time", name: () => t`Vehicle total engine time`, icon: <SettingOutlined /> },
];

const FormOverview = ({
  savedQuestions,
  additionalFields,
  formQuestions,
  loadingFormData,
  canEdit,
  refreshForm,
  formData,
  canUpdateState,
  resetQuestions,
  refreshAdditionalFields,
}: {
  canEdit: boolean;
  formQuestions: FormQuestion[];
  loadingFormData: boolean;
  savedQuestions: MutableRefObject<DraggableQuestion[] | null>;
  additionalFields: AdditionalField[];
  formData: NavirecForm | null | undefined;
  canUpdateState: boolean;
  refreshForm: () => void;
  resetQuestions: () => void;
  refreshAdditionalFields: () => void;
}) => {
  const { httpClient, configuration } = useApplicationContext();

  const { id } = router.query;
  const createNew = id === "new";

  const [form] = Form.useForm();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [questions, setQuestions] = useState<DraggableQuestion[]>(savedQuestions.current || []);
  const [postingForm, setPostingForm] = useState<boolean>(false);
  const [previewVisible, setPreviewVisbible] = useState<boolean>(false);
  const [additionalFieldModalVisible, setAdditionalFieldVisible] = useState<boolean>(false);
  const [additionalFieldInitalValues, setAdditionalFieldInitialValues] = useState<undefined | Partial<AdditionalField>>(
    undefined
  );
  const [selectedQuestion, setSelectedQuestion] = useState<string | undefined>(undefined);

  const activeQuestions = useMemo(() => questions?.filter((q) => q.active) || [], [questions]);
  const fieldOptions = getQuestionFieldOptions(activeQuestions);

  const additionalFieldOptions = useMemo(
    () =>
      additionalFields.map(({ name, url }) => ({
        label: name,
        value: url,
        disabled: !!questions.find((x) => x.additional_field === url),
      })),
    [additionalFields, questions]
  );

  useLayoutEffect(() => {
    if (formData) {
      form.setFieldsValue(formData);
    } else {
      form.resetFields();
    }
    return () => {
      form.resetFields();
      resetQuestions();
    };
  }, [formData]);

  useEffect(() => {
    if (!savedQuestions.current || isEmpty(savedQuestions.current)) {
      setQuestions(convertIntoDraggableQuestion(formQuestions));
    }
  }, [formQuestions]);

  useEffect(() => {
    savedQuestions.current = questions;
  }, [questions]);

  const onSubmit = useCallback(
    async ({ name, description }: { name: string; description?: string }, redirect = true) => {
      if (!name) {
        message.error(t`Name is required`);
        return;
      }
      setPostingForm(true);

      let formResponse = null;
      const requestConfiguration = {
        data: {
          name,
          description,
          account: configuration?.account?.url,
        },
      };

      if (createNew) {
        formResponse = await httpClient.post("forms/", requestConfiguration);
      } else {
        formResponse = await httpClient.patch(`forms/${id}/`, requestConfiguration);
      }

      if (formResponse) {
        if (questions) {
          let questionsCopy = [...(savedQuestions.current || [])];

          const questions = convertIntoFormQuestion(questionsCopy, formResponse.url as string);

          const questionsResponse = await httpClient.post("form_questions/", {
            data: questions,
            errorHandler: (err) => {
              if (err?.response?.data?.non_field_errors) {
                return message.error(err.response.data.non_field_errors?.[0]);
              }
              return message.error(t`Failed to save form`);
            },
          });

          if (!questionsResponse) {
            setPostingForm(false);
            return;
          }
        }

        setPostingForm(false);
        message.success(t`Form updated`);
        redirect && router.push("/forms");
        return formResponse;
      } else {
        message.error(t`Failed to save form`);
        setPostingForm(false);
        return undefined;
      }
    },
    [createNew]
  );

  // question methods
  const copyQuestion = useCallback(
    (questionKey: string) => {
      const questionsCopy = [...questions];
      const questionIndex = questionsCopy.findIndex((x) => x.key === questionKey);

      if (questionIndex === -1) {
        return;
      }

      questionsCopy.splice(questionIndex + 1, 0, omit({ ...questionsCopy[questionIndex], key: v4() }, "id"));

      setQuestions(questionsCopy);
    },
    [questions]
  );

  const deleteQuestion = useCallback(
    (questionKey: string) =>
      setQuestions((curr) => {
        const result: DraggableQuestion[] = [];

        curr.forEach((q) => {
          if (q.key === questionKey) {
            if (q.id) {
              result.push({ ...q, active: false });
            }
          } else {
            result.push(q);
          }
        });
        return result;
      }),
    []
  );

  const addQuestion = useCallback((type: DraggableQuestion["type"]) => {
    setQuestions((curr) => [
      ...curr,
      {
        key: v4(),
        type,
        question: FIELD_TYPES.find((x) => x.key === type)?.name() || t`New question`,
        sort_order: curr.length + 1,
        ...(CHOICE_FIELD_TYPES.includes(type) && { choices: [{ id: v4(), value: t`First option` }] }),
        active: true,
      },
    ]);
  }, []);

  const updateQuestion = useCallback((key: string, fieldName: string, value: any) => {
    setQuestions((curr) => {
      return curr.map((x) => {
        if (x.key === key) {
          return { ...x, [fieldName]: value };
        }

        return x;
      });
    });
  }, []);

  const triggerFormStateAction = useCallback(
    async (action: FormAction, id: string) => {
      if ((action === "archive" || action === "publish") && id) {
        setPostingForm(true);
        const isArchiving = action === "archive";
        message.loading(isArchiving ? t`Archiving form` : t`Publishing form`);

        const apiUrl = `forms/${id}/${isArchiving ? "archive" : "activate"}/`;

        const res = await httpClient.post(apiUrl, { data: { account: configuration?.account?.url } });

        setPostingForm(false);

        message.destroy();
        if (res) {
          refreshForm();
          message.success(t`Form was updated successfully!`);
          router.push("/forms");
        } else {
          message.error(t`Failed to update!`);
        }
      }
    },
    [configuration, id, refreshForm]
  );

  const renderActionMenuButtons = useCallback(() => {
    const buttons: ReactNode[] = [];

    if (formData?.state !== "archived") {
      buttons.push(
        <Button
          type="default"
          onClick={() => {
            return router.push(`/forms/`);
          }}
        >
          <Trans>Discard changes</Trans>
        </Button>
      );
    }

    if (!formData?.state) {
      buttons.push(
        <Button disabled={postingForm || !canEdit} onClick={() => form.submit()}>
          <Trans>Save as draft</Trans>
        </Button>
      );

      buttons.push(
        <Button
          disabled={postingForm || !canUpdateState}
          onClick={async () => {
            const { description, name } = form.getFieldsValue();
            const data = await onSubmit({ name, description }, false);
            if (data) {
              await triggerFormStateAction("publish", data.id);
            }
          }}
          type="primary"
        >
          <Trans>Publish</Trans>
        </Button>
      );

      return buttons;
    }

    if (formData?.state === "draft") {
      buttons.push(
        <Button disabled={postingForm || !canEdit} onClick={() => form.submit()}>
          <Trans>Save as draft</Trans>
        </Button>
      );
      buttons.push(
        <Button
          disabled={postingForm || !canUpdateState}
          onClick={() => triggerFormStateAction("publish", id as string)}
          type="primary"
        >
          <Trans>Publish</Trans>
        </Button>
      );
    }

    if (formData?.state === "active") {
      buttons.push(
        <Button disabled={postingForm || !canEdit} onClick={() => form.submit()} type="primary">
          <Trans>Save</Trans>
        </Button>
      );
    }

    if (formData.state !== "archived") {
      const items =
        formData.state === "active"
          ? [
              {
                key: 0,
                disabled: postingForm || !canUpdateState,
                label: (
                  <Popconfirm
                    disabled={postingForm || !canUpdateState}
                    onConfirm={() => triggerFormStateAction("archive", id as string)}
                    title={t`Do you want to arhive this form?`}
                    okText={t`Yes, arhice this form`}
                  >{t`Archive`}</Popconfirm>
                ),
                onClick: async () => {},
                danger: true,
              },
              {
                key: 1,
                label: (
                  <Link target="_blank" href={`/forms/${id}/responses/create`}>
                    <Typography.Text type="success">
                      <Trans>Create response</Trans>
                    </Typography.Text>
                  </Link>
                ),
              },
            ]
          : [
              {
                key: 0,
                disabled: postingForm || !canUpdateState,
                label: (
                  <Popconfirm
                    disabled={postingForm || !canUpdateState}
                    onConfirm={() => triggerFormStateAction("archive", id as string)}
                    title={t`Do you want to arhive this form?`}
                    okText={t`Yes, arhice this form`}
                  >{t`Archive`}</Popconfirm>
                ),
                onClick: async () => {},
                danger: true,
              },
            ];
      buttons.push(<MoreButton items={items} />);
    }

    return buttons;
  }, [formData?.state, form, triggerFormStateAction, canEdit]);

  return (
    <Spin spinning={loadingFormData}>
      {!canEdit ? (
        <div style={{ margin: 10 }}>
          <Alert type={"warning"} message={t`You do not have the permissions to edit this form`} />
        </div>
      ) : null}
      <Form onFinish={onSubmit} form={form}>
        <div style={{ padding: 16 }}>
          <Row gutter={16}>
            <Col flex={1}>
              <Form.Item labelCol={{ span: 24 }} label={t`Form name`} required name="name">
                <Input disabled={!canEdit} />
              </Form.Item>
            </Col>
            <Col>
              <Divider style={{ height: "100%" }} type="vertical" />
            </Col>
            <Col>
              <Row style={{ height: "100%" }} gutter={16}>
                <Col>
                  <Button
                    style={{ marginTop: 10 }}
                    onClick={() => setPreviewVisbible(true)}
                    icon={
                      <span style={{ paddingRight: 8 }}>
                        <EyeOutlined />
                      </span>
                    }
                  >
                    <Trans>Preview form</Trans>
                  </Button>
                  <Col style={{ marginTop: 10 }}>
                    <Typography.Text type="secondary">
                      <Trans>Preview how the form looks like</Trans>
                    </Typography.Text>
                  </Col>
                </Col>

                <Col>
                  <Divider style={{ height: "100%" }} type="vertical" />
                </Col>
              </Row>
            </Col>
          </Row>
          <ActionMenu menus={renderActionMenuButtons()} />
          <Form.Item labelCol={{ span: 24 }} name="description" label={t`Description`}>
            <Input.TextArea disabled={!canEdit} />
          </Form.Item>
          <Divider style={{ marginTop: 0, marginBottom: 16 }} orientation="left">
            <Trans>Form questions</Trans>
          </Divider>
          <DndContext
            sensors={sensors}
            onDragEnd={({ active, over }) => {
              if (over && active.id !== over?.id) {
                const activeIndex = questions.findIndex(({ key }) => key === active.id);
                const overIndex = questions.findIndex(({ key }) => key === over.id);

                setQuestions(arrayMove(questions, activeIndex, overIndex));
              }
            }}
          >
            <SortableContext items={activeQuestions.map((x) => ({ ...x, id: x.key }))}>
              {activeQuestions.map((q) => (
                <QuestionItem
                  openAdditionalFieldModal={() => {
                    setSelectedQuestion(q.key);
                    setAdditionalFieldVisible(true);
                    setAdditionalFieldInitialValues({
                      type: q.type === "number" ? "decimal" : "text",
                      available: ["vehicles"],
                      account: configuration?.account?.url,
                      name: q.question,
                      ...(q.choices && { choices: q.choices.map((x) => x.value) }),
                    });
                  }}
                  disabled={!canEdit}
                  canDuplicate={canDuplicate(q.type, activeQuestions)}
                  updateQuestion={(field: string, value: any) => updateQuestion(q.key, field, value)}
                  copyQuestion={copyQuestion}
                  fieldOptions={fieldOptions}
                  deleteQuestion={deleteQuestion}
                  additionalFieldOptions={additionalFieldOptions}
                  key={q.key}
                  question={q}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Dropdown
            placement="bottom"
            disabled={!canEdit}
            menu={{
              items: fieldOptions,
              onClick: ({ key }) => addQuestion(key as DraggableQuestion["type"]),
            }}
          >
            <Button disabled={!canEdit}>
              <Space>
                <Trans>Add question</Trans>
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        </div>
      </Form>
      <FormPreviewDrawer
        onCancel={() => setPreviewVisbible(false)}
        isVisible={previewVisible}
        questions={questions.filter((x) => x.active).map((x, i) => ({ ...x, sort_order: i })) || []}
        form={form.getFieldsValue()}
      />
      <AdditionalFieldModal
        useInFormView={true}
        initialValues={additionalFieldInitalValues}
        visible={additionalFieldModalVisible}
        onSuccess={(createdAdditionalField) => {
          refreshAdditionalFields();
          setQuestions((questions) => {
            return questions.map((question) => {
              if (question.key === selectedQuestion) {
                return { ...question, additional_field: createdAdditionalField.url };
              }
              return question;
            });
          });
        }}
        onClose={() => {
          setSelectedQuestion(undefined);
          setAdditionalFieldVisible(false);
          setAdditionalFieldInitialValues(undefined);
        }}
      />
    </Spin>
  );
};

export default FormOverview;
